import type { Database, TalentProfile } from '../../types';
import { safeParse, filterAndSortSkills } from './utils';

type CandidateRow = Database['public']['Tables']['candidates']['Row'];

interface RawCandidateJson {
  // ── Edge function (snake_case) ──────────────────────────────────────────────
  candidate_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  location?: string;
  suggested_title?: string;
  original_title?: string;
  total_experience_years?: number | string;
  seniority_level?: string;
  seniority_numeric?: number;
  job_family?: string;
  additional_insights?: string;
  profile_summary?: string;
  domain_tags?: string[];
  global_fit_score?: number;
  top_strengths_paragraph?: string;
  role_fit_titles?: string | string[];
  top_achievements?: string | string[];
  technical_skills?: any[];
  methods_tools_skills?: any[];
  soft_skills?: any[];
  experiences?: any[];
  global_tech_stack?: string[];
  global_methods_tools?: string[];
  global_soft_skills?: string[];
  alerts?: any[];
  languages?: any[] | string[];
  education?: any[];
  certifications?: any[];
  industries?: string[];

  // ── n8n legacy (PascalCase / mixed) ────────────────────────────────────────
  First_name_candidat?: string;
  Last_name_candidat?: string;
  Contact_phone?: string;
  Contact_email?: string;
  Contact_linkedin?: string;
  Suggested_title?: string;
  Years_experience?: number | string;
  Seniority_level?: string;
  Top_strengths?: string;
  Top_strengths_paragraph?: string;
  Profile_summary?: string;
  profil_summary?: string;
  Profil_summary?: string;
  additional_insight?: string;
  Additional_insight?: string;
  Role_fit_titles?: string | string[];
  top_strengths?: string;
  Industries_candidat?: string | string[];
  Alerts?: string | { message?: string };
  Global_fit_score?: string | number;
  Top_achievements?: string | string[];
  resume_profil?: string;
  Analyse_experience?: string | any[];
  Technical_competences_keys?: string | any[];
  Methods_competences_keys?: string | any;
  Soft_competences_keys?: string | any;
  Education?: string | any[];
  Certifications_candidat?: string | any[];
  Langues?: string | any[];

  // ── Other legacy ────────────────────────────────────────────────────────────
  name?: string;
  role?: string;
  headline?: string;
  experience?: string;
  badges?: string[];
  summary?: string;
  additionalInsight?: string;
  skills?: {
    technical?: { name: string; level: number }[];
    methods?: { name: string; level: number }[];
    soft?: { name: string; level: number }[];
  };
  differentiator?: string;
  timeline?: any[];
  contactInfo?: any;
  topAchievements?: string[];
}

export function mapDbCandidateToTalentProfile(row: CandidateRow): TalentProfile {
  let rawData = row.summary_json as unknown;
  if (typeof rawData === 'string') {
    rawData = safeParse(rawData) || {};
  }
  const data = (rawData as RawCandidateJson) || {};

  // Name — edge stores candidate_name + first_name/last_name, n8n uses First_name_candidat
  const firstName = data.first_name || data.First_name_candidat || '';
  const lastName = data.last_name || data.Last_name_candidat || '';
  const fullNameFromJson = data.candidate_name || (firstName + ' ' + lastName).trim();
  const name = row.full_name || fullNameFromJson || data.name || 'Unknown';

  // Dedicated columns (populated by trigger, always available even without summary_json)
  const role = (row as any).role_title || data.suggested_title || data.Suggested_title || data.role || '';

  let profile_summary: string =
    (data as any)['profil _summary'] ||
    data.profile_summary ||
    data.Profile_summary ||
    data.profil_summary ||
    data.Profil_summary ||
    data.resume_profil ||
    data.headline ||
    '';
  const parsedProfileSummary = safeParse(profile_summary);
  if (typeof parsedProfileSummary === 'string') {
    profile_summary = parsedProfileSummary;
  }

  const additional_insight =
    data.additional_insights ||
    data.additional_insight ||
    data.Additional_insight ||
    data.additionalInsight ||
    '';

  const rawRoleFitTitles = safeParse(data.role_fit_titles || data.Role_fit_titles);
  const role_fit_titles: string[] = Array.isArray(rawRoleFitTitles) ? rawRoleFitTitles : [];

  const top_strengths =
    data.top_strengths_paragraph ||
    data.top_strengths ||
    data.Top_strengths_paragraph ||
    data.Top_strengths ||
    '';

  // experience_years dedicated column first, fallback to summary_json
  let experience = '';
  const expYears = (row as any).experience_years;
  if (expYears != null) {
    experience = `${expYears} ans`;
  } else if (data.experience) {
    experience = data.experience;
  } else if (data.total_experience_years != null) {
    experience = `${data.total_experience_years} ans`;
  } else if (data.Years_experience != null) {
    experience = `${data.Years_experience} ans`;
  }

  const seniority_level = data.seniority_level || data.Seniority_level || '';
  const summary = data.resume_profil || data.summary || data.top_strengths_paragraph || data.Top_strengths_paragraph || '';

  // domain_tags dedicated column first (array), fallback to summary_json
  let badges: string[] = (row as any).domain_tags || [];
  if (badges.length === 0) {
    if (Array.isArray(data.domain_tags) && data.domain_tags.length > 0) {
      badges = data.domain_tags;
    } else {
      const rawIndustries = safeParse(data.Industries_candidat);
      if (Array.isArray(rawIndustries)) {
        badges = rawIndustries;
      } else if (data.badges) {
        badges = data.badges;
      }
    }
  }

  let score: number | undefined;
  const rawFitScore = data.global_fit_score ?? data.Global_fit_score;
  if (rawFitScore != null) {
    const parsed = Number(rawFitScore);
    if (!isNaN(parsed)) score = parsed;
  }

  // Timeline — edge uses experiences[], n8n uses Analyse_experience
  let timeline: any[] = [];
  const edgeExperiences = data.experiences;
  if (Array.isArray(edgeExperiences) && edgeExperiences.length > 0) {
    timeline = edgeExperiences.map((item: any) => ({
      year: `${item.start_date || ''} - ${item.end_date || 'Présent'}`,
      role: item.role || '',
      company: item.company || '',
      tags: Array.isArray(item.main_technologies) ? item.main_technologies : [],
      methodTags: [],
      softTags: [],
      summary: item.summary || '',
    }));
  } else {
    const rawTimeline = safeParse(data.Analyse_experience);
    if (Array.isArray(rawTimeline)) {
      timeline = rawTimeline.map((item: any) => ({
        year: `${item.start_date} - ${item.end_date || 'Present'}`,
        role: item.role || '',
        company: item.company || '',
        tags: Array.isArray(item.main_technologies) ? item.main_technologies : [],
        methodTags: Array.isArray(item.main_methods) ? item.main_methods : [],
        softTags: Array.isArray(item.main_soft) ? item.main_soft : [],
        summary: item.summary || '',
      }));
    } else if (data.timeline) {
      timeline = data.timeline;
    }
  }

  const mapSkill = (item: any) => ({
    name: item.name,
    level: Number(item.score ?? item.level) || 0,
    category: item.category || '',
    evidence: item.evidence || '',
  });

  // top_skills dedicated column: [{name, level, type}] — used in list view when summary_json absent
  const rowTopSkills: any[] = (row as any).top_skills || [];

  let technicalSkills: any[] = [];
  if (Array.isArray(data.technical_skills) && data.technical_skills.length > 0) {
    technicalSkills = data.technical_skills.map(mapSkill);
  } else {
    const rawTechnical = safeParse(data.Technical_competences_keys);
    technicalSkills = Array.isArray(rawTechnical)
      ? rawTechnical.map(mapSkill)
      : data.skills?.technical || rowTopSkills.filter((s: any) => s.type === 'technical').map(mapSkill);
  }
  technicalSkills = filterAndSortSkills(technicalSkills);

  let methodsSkills: any[] = [];
  if (Array.isArray(data.methods_tools_skills) && data.methods_tools_skills.length > 0) {
    methodsSkills = data.methods_tools_skills.map(mapSkill);
  } else {
    const rawMethods = safeParse(data.Methods_competences_keys);
    if (rawMethods) {
      const list = Array.isArray(rawMethods) ? rawMethods : [rawMethods];
      methodsSkills = list.map(mapSkill);
    } else {
      methodsSkills = data.skills?.methods || rowTopSkills.filter((s: any) => s.type === 'methods').map(mapSkill);
    }
  }
  methodsSkills = filterAndSortSkills(methodsSkills);

  let softSkills: any[] = [];
  if (Array.isArray(data.soft_skills) && data.soft_skills.length > 0) {
    softSkills = data.soft_skills.map(mapSkill);
  } else {
    const rawSoft = safeParse(data.Soft_competences_keys);
    if (rawSoft) {
      const list = Array.isArray(rawSoft) ? rawSoft : [rawSoft];
      softSkills = list.map(mapSkill);
    } else {
      softSkills = data.skills?.soft || rowTopSkills.filter((s: any) => s.type === 'soft').map(mapSkill);
    }
  }
  softSkills = filterAndSortSkills(softSkills);

  // P1.0ter — si un scoring déterministe-ancré existe (colonne scored_skills),
  // il PRIME sur les scores 0-100 bruts du summary_json pour l'affichage.
  // Niveaux 0-10 → ×10 pour les barres ; groupés par « Prouvé » / « Déduit ».
  let skillsScored = false;
  const scored = (row as any).scored_skills as
    | { technical_skills?: any[]; methods_tools_skills?: any[]; soft_skills?: any[] }
    | null;
  if (scored && Array.isArray(scored.technical_skills)) {
    const mapScored = (item: any) => ({
      name: item.name,
      level: (Number(item.level) || 0) * 10,
      category: '', // le scoring ne porte pas de sous-catégorie ; on affiche une liste triée par niveau
      evidence: item.reasoning || '',
      inferred: typeof item.inferred === 'boolean' ? item.inferred : undefined,
    });
    const clean = (arr: any[]) =>
      (arr || []).map(mapScored).filter((s) => s.level > 0).sort((a, b) => b.level - a.level);
    technicalSkills = clean(scored.technical_skills);
    methodsSkills = clean(scored.methods_tools_skills);
    softSkills = clean(scored.soft_skills);
    skillsScored = true;
  }

  // Education — edge returns array of objects directly, n8n uses Education (serialized)
  let education: any[] = [];
  if (Array.isArray(data.education) && data.education.length > 0) {
    education = data.education.map((item: any) => ({
      degree: item.degree || '',
      school: item.school || '',
      year: item.graduation_year || item.year || '',
    }));
  } else {
    const rawEducation = safeParse(data.Education);
    if (Array.isArray(rawEducation)) {
      education = rawEducation.map((item: any) => ({
        degree: item.degree || '',
        school: item.school || '',
        year: item.graduation_year || '',
      }));
    }
  }

  // Certifications — edge returns [{name, issuer, year}], n8n uses Certifications_candidat
  let certifications: string[] = [];
  if (Array.isArray(data.certifications) && data.certifications.length > 0) {
    certifications = data.certifications.map((item: any) =>
      typeof item === 'string' ? item : item.name || ''
    );
  } else {
    const rawCertifications = safeParse(data.Certifications_candidat);
    if (Array.isArray(rawCertifications)) {
      certifications = rawCertifications.map((item: any) => item.name || '');
    }
  }

  // Languages — edge returns [{language, level}], n8n uses Langues
  let languages: string[] = [];
  if (Array.isArray(data.languages) && data.languages.length > 0) {
    languages = data.languages.map((item: any) =>
      typeof item === 'string' ? item : `${item.language} (${item.level})`
    );
  } else {
    const rawLanguages = safeParse(data.Langues);
    if (Array.isArray(rawLanguages)) {
      languages = rawLanguages.map((item: any) => `${item.language} (${item.level})`);
    }
  }

  // Alerts — edge returns [{type, severity, message}], n8n uses Alerts (object or string)
  let alerts: string[] = [];
  if (Array.isArray(data.alerts) && data.alerts.length > 0) {
    alerts = data.alerts.map((item: any) =>
      typeof item === 'string' ? item : item.message || ''
    ).filter(Boolean);
  } else {
    const rawAlerts = safeParse(data.Alerts);
    if (rawAlerts && typeof rawAlerts === 'object' && (rawAlerts as any).message) {
      alerts = [(rawAlerts as any).message];
    }
  }

  // Top achievements — edge uses top_achievements, n8n uses Top_achievements
  let topAchievements: string[] = [];
  const rawAchievements = safeParse(data.top_achievements ?? data.Top_achievements);
  if (Array.isArray(rawAchievements)) {
    topAchievements = rawAchievements;
  } else if (data.topAchievements) {
    topAchievements = data.topAchievements;
  }

  // Availability & TJM: dedicated columns take priority over summary_json (survive re-parse)
  const availability_status =
    (row as any).availability_status ??
    (sj => {
      const v = sj.availability_status ?? sj.availability ?? sj.disponibilite ?? sj.disponibility;
      if (!v) return null;
      if (/dispon/i.test(v)) return 'disponible';
      if (/listen|ecoute|écoute/i.test(v)) return 'listening';
      if (/indispon|unavail/i.test(v)) return 'unavailable';
      return null;
    })(data as any) ?? null;

  const availability_type =
    (row as any).availability_type ??
    (sj => {
      const v = (sj as any).availability_type ?? (sj as any).contract_type ?? (sj as any).type_contrat;
      if (!v) return null;
      if (/freelance/i.test(v)) return 'freelance';
      if (/cdi/i.test(v)) return 'cdi';
      if (/both|les.?2|les.?deux/i.test(v)) return 'both';
      return null;
    })(data as any) ?? null;

  const availability_date =
    (row as any).availability_date ??
    (data as any).availability_date ??
    (data as any).available_from ??
    null;

  const rawTjmMin = (row as any).tjm_min ?? (data as any).tjm_min ?? (data as any).TJM_min ?? (data as any).daily_rate_min ?? (data as any).tarif_min ?? null;
  const rawTjmMax = (row as any).tjm_max ?? (data as any).tjm_max ?? (data as any).TJM_max ?? (data as any).daily_rate_max ?? (data as any).tarif_max ?? null;
  const tjm_min = rawTjmMin !== null ? Number(rawTjmMin) || null : null;
  const tjm_max = rawTjmMax !== null ? Number(rawTjmMax) || null : null;

  return {
    id: row.id.toString(),
    updatedAt: (row as any).updated_at || (row as any).created_at || '',
    name,
    role,
    experience,
    seniority_level,
    badges,
    summary,
    profile_summary,
    additional_insight,
    role_fit_titles,
    top_strengths,
    score,
    skills: {
      technical: technicalSkills,
      methods: methodsSkills,
      soft: softSkills,
    },
    skillsScored,
    differentiator: data.differentiator || '',
    timeline,
    // `contactInfo` est la couche SAISIE PAR L'HUMAIN ; le reste vient de l'extraction du
    // CV. L'humain a toujours raison : il passe donc en premier. L'ordre inverse rendait
    // toute correction invisible dès que le CV portait déjà la donnée — soit 57 fiches
    // sur 77 pour la localisation.
    // L'email fait exception : `row.email` est une vraie colonne, contrainte d'unicité et
    // clé de déduplication. Il ne se corrige pas depuis ce bloc, il reste en tête.
    contactInfo: {
      email: row.email || data.contactInfo?.email || data.email || data.Contact_email || '',
      phone: data.contactInfo?.phone || data.phone || data.Contact_phone || '',
      location_city: data.contactInfo?.location_city || data.location || '',
      linkedin_url: data.contactInfo?.linkedin_url || data.linkedin || data.Contact_linkedin || '',
    },
    topAchievements,
    languages,
    education,
    certifications,
    alerts,
    status: row.status,
    filename: row.filename,
    content_hash: (row as any).content_hash ?? null,
    assigned_to: (row as any).assigned_to ?? null,
    availability_status: availability_status as 'disponible' | 'listening' | 'unavailable' | null,
    availability_type: availability_type as 'freelance' | 'cdi' | 'both' | null,
    availability_date,
    tjm_min,
    tjm_max,
    photo_url: (row as any).photo_url ?? null,
  };
}
